import { Flex, IconButton } from '@chakra-ui/react'

import { useCopyToClipboard } from '../../helpers'
import { Icon } from '../Icon'
import { Tooltip } from '../Tooltip'
import HeroButton from './HeroButton'

export interface IHeroLinkButtonProps {
  /** Texto do botão (`Página do evento`). */
  label: string
  /** URL pública. Abre em nova aba no botão, e vai para a área de transferência no ícone ao lado. */
  url: string
  icon?: string
}

/**
 * Par "abrir a página pública" + "copiar o link" da fileira de ações do `<DetailHero>`.
 *
 * Era o `ContestLinkRow` e o `EventLinkRow`, o mesmo componente em dois arquivos. O segundo
 * tinha ainda um ramo `copyOnly` inteiro — outro botão, com o rótulo virando "Link copiado!" —
 * que nunca foi usado por nenhum call site: a prop não aparece em lugar nenhum do painel. Ele
 * não sobreviveu à junção.
 *
 * O botão de copiar é o `useCopyToClipboard` com a aparência do banner, não o `<CopyButton>`:
 * sobre o `gray.900` a cor do registry seria a única coisa colorida da faixa — a mesma decisão
 * que o `<HeroButton>` já toma ao usar `whiteAlpha`. O que o hook traz é a guarda, o `await` e
 * o aviso quando a escrita falha, que o `useClipboard` do Chakra daqui não dava.
 */
export default function HeroLinkButton({ label, url, icon = 'lni lni-link' }: IHeroLinkButtonProps) {
  const { copied, copy } = useCopyToClipboard()

  return (
    <Flex align="center" gap={1}>
      <HeroButton icon={icon} href={url}>
        {label}
      </HeroButton>
      {/* `closeOnClick={false}`: o tooltip precisa continuar aberto para mostrar o "Copiado!". */}
      <Tooltip label={copied ? 'Copiado' : 'Copiar link'} closeOnClick={false}>
        <IconButton
          aria-label="Copiar link"
          icon={<Icon name={copied ? 'lni lni-checkmark' : 'lni lni-clipboard'} size="md" />}
          size="sm"
          variant="ghost"
          color={copied ? 'green.300' : 'whiteAlpha.600'}
          _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
          onClick={() => copy(url, { entity: 'link', description: url })}
        />
      </Tooltip>
    </Flex>
  )
}
